import { Component, DoCheck, OnInit } from '@angular/core';
import { NavigationEvent } from "@spartacus/storefront";
import { filter, switchMap, take, tap } from "rxjs/operators";
import { EventService, GlobalMessageService, GlobalMessageType, OccEndpointsService, UserIdService, WindowRef } from "@spartacus/core";
import { AuthService } from '@auth0/auth0-angular';
import { combineLatest, EMPTY, Observable, Subscription } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { NavigationEnd, Router } from '@angular/router';
import { ActiveCartFacade, MultiCartFacade } from '@spartacus/cart/base/root';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { GenBaseStoreService } from './generac/services/base-store.service';
import { GenCheckoutService } from './generac/services/checkout.service';
import { GenCheckoutMonitorService } from './generac/services/checkout-monitor.service';
import { GenCustomerService } from './generac/services/customer.service';
import { GenGoogleAnalyticsService } from './generac/services/google-analytics.service';
import packageJson from '../../package.json';
import { GenAutoShipActiveCartService } from './generac/services/auto-ship-active-cart.service';


@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, DoCheck {
  title = 'spartacusstore';
  subscriptions = new Subscription();

  constructor(
    private eventService: EventService,
    private googleAnalyticsService: GenGoogleAnalyticsService,
    private baseStoreService: GenBaseStoreService,
    private http: HttpClient,
    public auth: AuthService,
    private router: Router,
    private occEndpoints: OccEndpointsService,
    private multiCartFacade: MultiCartFacade,
    protected customerService: GenCustomerService,
    private userAccount: UserAccountFacade,
    private activeCartFacade: ActiveCartFacade,
    private userIdService: UserIdService,
    private windowRef: WindowRef,
    private checkoutMonitorService: GenCheckoutMonitorService,
    private checkoutService: GenCheckoutService,
    private globalMessageService: GlobalMessageService,
    private autoShipActiveCartService: GenAutoShipActiveCartService,
  ) {
  }

  ngOnInit(): void {
    this.autoShipActiveCartService.initialize();
    this.baseStoreService.setCurrentSite();
    this.subscribeToSearchEvent();
    this.checkReOrdering();
    this.reOrderByOrderNbrState();
    this.logTheApplicationVersion();
    this.monitorWellsFargoCheckout();
  }

  ngDoCheck(): void {
    if (this.windowRef.isBrowser()) {
      const location = this.windowRef.nativeWindow.location;
      const isAutoShipCart = location.pathname.replace(/\/$/, '') === '/cart'
        && new URLSearchParams(location.search).get('autoShip') === 'true';
      const classList = this.windowRef.document.body.classList;
      if (classList.contains('auto-ship-cart-page') !== isAutoShipCart) {
        classList.toggle('auto-ship-cart-page', isAutoShipCart);
      }
    }
  }

  monitorWellsFargoCheckout(): void {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const url = event.urlAfterRedirects;
        const inAnyCheckoutStep = url.startsWith('/checkout');
        const isAttestationStep = url.startsWith('/checkout/engine-replacement-attestation');
        const inDeliveryAddressStep = url.startsWith('/checkout/delivery-address');
        const inOrderConfirmation = url.startsWith('/order-confirmation');
        if ((!inAnyCheckoutStep || (inDeliveryAddressStep || isAttestationStep)) && !inOrderConfirmation) {
          if (this.checkoutMonitorService.shouldTrackAbandonment()) {
            this.checkoutMonitorService.setOngoingAbandonment(true);
            this.handleCheckoutAbandonment();
            if (inDeliveryAddressStep || isAttestationStep) {
              this.router.navigate(['/cart']);
            }
          }
        }
      }
    });
  }

  handleCheckoutAbandonment(): void {
    combineLatest([
      this.userIdService.takeUserId().pipe(filter(v => !!v)),
      this.activeCartFacade.getActiveCartId().pipe(filter(v => !!v)),
    ]).pipe(
      take(1),
      tap(([userId, cartId]) => {
        if (cartId) {
          this.checkoutService.revertSanitizedWellsFargoCart(cartId, userId).pipe(
            take(1)
          ).subscribe({
            next: (cart: any) => {
              this.multiCartFacade.loadCart({
                cartId: cart.code,
                userId,
                extraData: {active: true}
              });
              this.activeCartFacade.getActive().subscribe();
              this.globalMessageService.add(
                {
                  key: 'checkout.wellsFargoCheckoutAbandonment'
                },
                GlobalMessageType.MSG_TYPE_WARNING
              );
              this.checkoutMonitorService.setOngoingAbandonment(false);
            }
          });
        }
      })
    ).subscribe();
  }

  private reOrderByOrderNbrState() {
    this.subscriptions.add(
      this.auth.appState$
        .pipe(
          take(1),
          switchMap(appState => {
            if (appState.orderNbr) {
              return this.reOrder(appState.orderNbr);
            }
            return EMPTY;
          }),
        )
        .subscribe()
    );
  }

  private checkReOrdering(): void {
    const search = new URLSearchParams(this.windowRef.location.search);
    const queryParams: { [key: string]: string } = Object.fromEntries(search as any);

    this.subscriptions.add(
      this.userAccount.get().pipe(
        filter((user) => !!user),
        take(1),
        switchMap(_ => {
          if (queryParams.companyNumber) {
            return this.customerService.setB2BUnit(queryParams.companyNumber, 'current').pipe(
              take(1),
              filter(() => !!queryParams.orderNbr),
              switchMap(() => this.reOrder(queryParams.orderNbr))
            );
          }
          return EMPTY;
        })
      ).subscribe()
    );
  }

  private subscribeToSearchEvent(): void {
    this.eventService
      .get(NavigationEvent)
      .pipe(
        filter(
          (navigationEvent: NavigationEvent) =>
            !!navigationEvent?.semanticRoute?.includes('search')
        )
      ).subscribe((e) => {
        this.googleAnalyticsService.sendGaEvent('search', {
          search_term: e?.params?.query
        });
    });
  }

  private reOrder(orderNbr: string): Observable<any> {
    return combineLatest([
      this.userIdService.takeUserId().pipe(filter(v => !!v)),
      this.activeCartFacade.getActiveCartId().pipe(filter(v => !!v)),
    ]).pipe(
      take(1),
      tap(([userId, cartId]) => {
        if (cartId) {
          this.multiCartFacade.deleteCart(cartId, userId);
        }
      }),
      switchMap(() => this.http.post(
        `${this.occEndpoints.getBaseUrl()}/orgUsers/current/cartFromOrder?orderCode=${orderNbr}`,
        {}
      )),
      tap(() => this.router.navigate(['/cart']))
    )
  }

  private logTheApplicationVersion() {
    console.log('Current application version => ', packageJson?.version);
  }

}
